import { createResource, createMemo, Accessor } from "solid-js";
import {
  fetchEligibility,
  fetchAgreementSigned,
} from "@/services/eligibility.service";
import { Permission } from "@/Main";

interface UseEligibilityChecksProps {
  dealerId: string;
  userId: string;
  userIsAdmin: boolean;
  permissions?: Record<string, Permission>;
}

interface UseEligibilityChecksReturn {
  eligibility: Accessor<boolean | undefined>;
  adminAccess: Accessor<boolean>;
  agreementSigned: Accessor<boolean | undefined>;
  allChecksPassed: Accessor<boolean>;
  eligibilityError: Accessor<Error | undefined>;
  agreementError: Accessor<Error | undefined>;
  shouldShowPlaceholder: Accessor<boolean>;
  hasEligibilityErrors: Accessor<boolean>;
  isLoadingEligibility: Accessor<boolean>;
}

/**
 * Orchestrates eligibility checks in an optimized order to minimize API calls.
 *
 * CHECK ORDER (Most Efficient → Least Efficient):
 * 1. Admin Access Check (instant - uses userIsAdmin prop or userId allowlist, no API call)
 * 2. Product Eligibility Check (API call - only runs if admin check passes)
 * 3. Agreement Signed Check (API call - only runs if both previous checks pass)
 * *
 * @param props.dealerId - Dealer identifier
 * @param props.userIsAdmin - Admin access flag from host app (no API call needed)
 * @param props.userId - User identifier, checked against allowlist for admin access
 * @returns Object with eligibility state accessors and loading/error states
 */

export const useEligibilityChecks = (
  props: UseEligibilityChecksProps,
): UseEligibilityChecksReturn => {
  const adminAccess = () =>
    props.userIsAdmin ||
    props.permissions?.["PRODUCT_ENROLLMENT"]?.accessType === "1";

  const [eligibility] = createResource(
    () => (adminAccess() ? props.dealerId : null),
    async (dealerId) => {
      if (!dealerId) return false;
      return fetchEligibility(dealerId);
    },
  );

  const [agreementSigned] = createResource(
    () => (adminAccess() && eligibility() === true ? props.dealerId : null),
    async (dealerId) => {
      if (!dealerId) return false;
      return fetchAgreementSigned(dealerId);
    },
  );

  const allChecksPassed = createMemo(
    () =>
      adminAccess() === true &&
      eligibility() === true &&
      agreementSigned() === true,
  );

  const eligibilityError = () => eligibility.error;
  const agreementError = () => agreementSigned.error;

  const shouldShowPlaceholder = createMemo(() => {
    return (
      adminAccess() === false ||
      eligibility() === false ||
      agreementSigned() === false
    );
  });

  const hasEligibilityErrors = createMemo(
    () => !!eligibilityError() || !!agreementError(),
  );

  const isLoadingEligibility = createMemo(
    () => eligibility.loading || agreementSigned.loading,
  );

  return {
    eligibility,
    adminAccess,
    agreementSigned,
    allChecksPassed,
    eligibilityError,
    agreementError,
    shouldShowPlaceholder,
    hasEligibilityErrors,
    isLoadingEligibility,
  };
};
